import React, { useState, useEffect, useRef, useCallback } from 'react';
import { matchPath } from 'react-router';
import { useLocation } from 'react-router-dom';

import {
  global,
  side_logout,
  side_asset,
  side,
  other,
  newReport,
} from 'config/Routes';
import Header from 'components/commons/Header';
import Footer from 'components/commons/Footer';
import Menu from 'components/commons/Menu';
import Main from 'components/commons/Main';

import PointToast, {
  PointItem,
} from 'components/features/poin-toast/PointToast';
import { useHeader, types as HeaderTypes } from 'hooks/useHeader';
import { useLoading } from 'hooks/useLoadign';
import { useRouting } from 'hooks/useRouting';
import PremiumModal from 'components/features/modal/PremiumModal';
import Loading from 'components/commons/loading/Loading';
import CustomHeader from 'components/commons/header/CustomHeader';
import { isPremium } from 'components/features/utils/userStatus';
import { initializeScrolTop } from 'utils/dom';
import { useAppUIContext } from 'contexts/AppUIContext';
import { useCustomizeHeaderContext } from 'contexts/CustomizeHeaderContext';
import { useGlobalMenuContext } from 'contexts/GlobalMenuContext';
import { HeadBlock } from './components/HeadBlock';

const App: React.FC = props => {
  const location = useLocation();

  const { uiClassName: uiClassName_ } = useAppUIContext();
  const { setWithNavigation } = useCustomizeHeaderContext();
  const { isShowMenu, setIsShowMenu } = useGlobalMenuContext();
  const [pointItems, setPointItems] = useState<PointItem[]>([]);
  const [toastsView, setToastsView] = useState<JSX.Element[]>();
  const [showPremium, setShowPremium] = useState<boolean>(false);
  const [path, setPath] = useState<string>('');

  const [header] = useHeader();
  const [loading] = useLoading();

  useEffect(() => {
    initializeScrolTop();
  }, [location.pathname]);

  // プレミアムダイアログ非表示
  const onClosePremium = (): void => {
    setShowPremium(false);
  };

  // メニュー選択
  const onNavLinkClick = (e: React.MouseEvent, path: string): void => {
    const isShowCancel =
      !isPremium() && !showPremium && (path === '/advice' || path === '/point');

    if (isShowCancel) {
      // プレミアムダイアログを表示
      setShowPremium(true);
      // リンク遷移のキャンセル
      e.preventDefault();
    }
  };

  const scrollable = useRef<HTMLDivElement>(null);
  const isNoHeader = useRef<boolean>(false);

  const routing = useRouting();

  // ルーティング設定
  useEffect(() => {
    // グローバルのメニューの場合はルーティング開始とする
    if (global.some(item => item.path === location.pathname)) {
      routing.setFirstRoute({ path: location.pathname }, false);
    } else {
      routing.pushRouting(
        { path: location.pathname, state: location.state },
        false
      );
    }
  }, [location.pathname, location.state, routing]);

  useEffect(() => {
    // ダミーのトースター設定
    // false && setTimeout(() => {
    //   const datPoint = [
    //     { type: 1, message: 'ログインスタンプが１つ\r\nたまりました', show: true },
    //     { type: 2, message: '取引回数50回達成！\r\n100ポイントをゲットしました', show: true },
    //     { type: 1, message: 'ログインスタンプが１つ', show: true },
    //     { type: 2, message: '取引回数50回達成！\r\n100ポイントをゲットしました\r\n100ポイントをゲットしました\r\n100ポイントをゲットしました', show: true },
    //   ]
    //   const dataPoint: PointItem[] =datPoint.map((value, index) => {
    //     return { ...value, index: index }
    //   })
    //   setPointItems(dataPoint);
    // }, 3000);
  }, []);

  // トースターのクローズ
  const onClosePointToast = useCallback(
    (current: PointItem | undefined): void => {
      // 1つのトースタをクロースする場合:
      //    cssで定義しているfadeOutの時間が 300ms なのでそれより少し短くしてフェードアウトを表現
      //    (同じ時間を設定すると設定前の状態で、表示されてしまう)
      // 全てのトースタをまとめて消す場合:
      //    cssで定義しているfadeOutの時間を待たずに、即時実行する感じ
      const wait_ms = current != null ? 250 : 0;

      setTimeout(() => {
        const targetIndex = current
          ? pointItems.findIndex(p => p.index === current?.index)
          : -1;
        setPointItems(currentPointItems => {
          const newItems = JSON.parse(JSON.stringify(currentPointItems));
          if (targetIndex >= 0) {
            // 個別に閉じる
            newItems[targetIndex] = { ...newItems[targetIndex], show: false };
          } else {
            // 全て閉じる
            for (let n = 0; n < newItems.length; n++) {
              newItems[n] = { ...newItems[n], show: false };
            }
          }
          return newItems;
        });
      }, wait_ms);
    },
    [pointItems]
  );

  useEffect(() => {
    // トースター表示位置（１つクローズされたら詰めて表示）
    const toastsView = pointItems
      .filter(item => item.show === true)
      .map((value, index) => {
        let p = 0;
        if (index > 0) {
          for (let n = 0; n < index; n++) {
            p += 90;
            const cnt = pointItems[n].message.match(/\r\n/g);
            if (cnt && cnt?.length >= 2) {
              p += (cnt?.length - 1) * 15;
            }
          }
        }
        return (
          <PointToast
            item={value}
            positionVertical={p}
            onClose={onClosePointToast}
            key={index}
          />
        );
      });
    setToastsView(toastsView);
  }, [pointItems, onClosePointToast]);

  useEffect(() => {
    const captured = scrollable.current;

    /* 
      ページ遷移の際のスクロール位置の調整
　      遷移を行った際に、
  　    　遷移先の画面がメニュー表示を伴う画面
    　  　遷移元の画面でメニューをフローティングにしている
      　　コンテンツの高さがメニューをフローティングさせる高さよりも大きい
      　場合に、メニューをフローティング開始するスクロール位置に移動させる
      　そうでない場合は、一番上に移動させる
    */
    // TODO: コンポーネントの高さを取る
    const headerHeight = 56;
    const screenHeight = captured?.clientHeight;
    const contentHeight = captured?.scrollHeight;
    const offset =
      isShowMenu &&
      isNoHeader.current &&
      screenHeight != null &&
      contentHeight != null &&
      screenHeight + headerHeight <= contentHeight
        ? headerHeight
        : 0;
    captured?.scrollTo(0, offset);

    return (): void => {
      const currentOffset = captured?.scrollTop;
      if (currentOffset != null) {
        isNoHeader.current = headerHeight <= currentOffset;
      }
    };
  }, [isNoHeader, isShowMenu, location.pathname, location.search, path]);

  useEffect(() => {
    if (location.pathname) {
      // リンク遷移したので、pathを更新
      setPath(`/${location.pathname.split('/')[1]}`);
    }
  }, [location.pathname]);

  useEffect(() => {
    // TODO:　取引詳細、目標詳細画面、口座連携案内のみ例外処理
    if (
      location.pathname.startsWith('/tradedetail') ||
      location.pathname.startsWith('/advice/operation') ||
      location.pathname.startsWith('/accountlinkageinfo')
    ) {
      setWithNavigation(true);
    } else {
      setWithNavigation(
        global.some(
          item =>
            item.path === location.pathname ||
            (item.subActivePaths &&
              item.subActivePaths.includes(location.pathname))
        )
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  useEffect(() => {
    if (
      global.some(
        item =>
          item.path === location.pathname ||
          (item.subActivePaths &&
            item.subActivePaths.includes(location.pathname))
      ) ||
      location.pathname.startsWith('/advice/operation') ||
      location.pathname.startsWith('/grouping')
    )
      return setIsShowMenu(true);
  }, [location.pathname, setIsShowMenu]);

  const routeInfo = global
    .concat(side_logout, side_asset, side, other, newReport)
    .find(item =>
      matchPath(location.pathname, {
        path: item.path,
      })
    );

  const isAdvice = path === '/advice';
  const uiClassName =
    uiClassName_ ??
    (isAdvice
      ? ''
      : // path === '/home' ||
      path === '/tradehistory' ||
        path === '/assettransition' ||
        path === '/advice'
      ? 'ui-app-background'
      : path === '/point'
      ? 'ui-point-bg'
      : '');
  const isAccountdetail = path === '/accountdetail';
  const isGrouping = path === '/grouping';
  const isNotificationList = location.pathname.startsWith('/notificationList');
  const groupingClass = 'grouping';
  const showFooterMarginTop = !isGrouping && !isNotificationList;
  const adviceClass = 'advice flex flex-col';
  const accountDetailClass = 'accountdetail-bgc';
  const isOptionalAssetRegistration = path === '/optionalassetregistration';
  const isOptionalAssetRenewal = path === '/optionalassetrenewal';

  return (
    <>
      <HeadBlock />
      <div
        className={[
          'flex flex-col scrollable',
          isAccountdetail && !loading.loading
            ? `h-auto min-h-full ${accountDetailClass}`
            : 'h-full',
        ]
          .filter(x => x)
          .join(' ')}
        ref={scrollable}>
        <div
          className={[
            'flex-grow',
            isAdvice && adviceClass,
            isGrouping && groupingClass,
            isOptionalAssetRegistration && 'optionalassetregistration',
            isOptionalAssetRenewal && 'optionalassetrenewal',
          ]
            .filter(x => x)
            .join(' ')}>
          {header.type === HeaderTypes.default && (
            <>
              {isShowMenu ? (
                <>
                  <Header
                    title={routeInfo?.title ?? ''}
                    path={path}
                    className={'border-b-2 border-gray-300'}
                    onNavLinkClick={onNavLinkClick}
                  />
                  <div className="sticky top-0 menu-sticky">
                    <Menu className={null} onNavLinkClick={onNavLinkClick} />
                  </div>
                </>
              ) : (
                <>
                  <div className="sticky top-0 menu-sticky">
                    <Header
                      title={routeInfo?.title ?? ''}
                      path={path}
                      className={'border-b-2 border-gray-300'}
                      onNavLinkClick={onNavLinkClick}
                    />
                  </div>
                </>
              )}
            </>
          )}
          {header.type === HeaderTypes.customContents && (
            <CustomHeader>{header.children}</CustomHeader>
          )}
          <Loading
            loading={loading.loading}
            style={{
              alignItems: 'normal',
              marginTop: '100px',
            }}>
            <div className={`${uiClassName}`} />
            <Main />
          </Loading>
          {toastsView}
          {showPremium && (
            <PremiumModal layoutPattern={2} onClose={onClosePremium} />
          )}
        </div>
        <Footer showFooterMarginTop={showFooterMarginTop} />
      </div>
    </>
  );
};

export default App;
